import { Field, FieldRenderProps } from "react-final-form";

import {
  ChoiceGroupOption,
  ChoiceGroupOptionProps,
  IChoiceGroupOption
} from "@bps/fluent-ui";
import { useSubmissionFormMetaContext } from "@ui-components/form/submission-form/context/SubmissionFormMetaContext.tsx";

import { ExposedFieldProps } from "./FinalForm.tsx";

type HtmlElementType = HTMLElement | HTMLInputElement;

export type ChoiceGroupOptionAdapterProps = Omit<
  ChoiceGroupOptionProps,
  | "value"
  | "defaultValue"
  | "defaultChecked"
  | "defaultSelectedKey"
  | "required"
  | "checked"
  | "onChange"
  | "text"
> & { label?: string };

export type ChoiceGroupOptionFieldProps = ExposedFieldProps<
  ChoiceGroupOptionProps["itemKey"],
  HtmlElementType
> &
  ChoiceGroupOptionAdapterProps;

const ChoiceGroupOptionAdapter = (
  props: FieldRenderProps<ChoiceGroupOptionProps["itemKey"], HtmlElementType> &
    ChoiceGroupOptionAdapterProps
) => {
  const { input, readOnly, itemKey, disabled, label, styles, size } = props;

  const formMeta = useSubmissionFormMetaContext();

  const onChange = (
    ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
    option?: IChoiceGroupOption
  ) => {
    input.onChange(option?.key);
  };

  const id = input.name + itemKey;

  return (
    <ChoiceGroupOption
      text={label ?? ""}
      itemKey={itemKey}
      disabled={
        readOnly || formMeta?.readonly || disabled || formMeta?.disabled
      }
      onChange={onChange}
      checked={itemKey === input.value}
      onBlur={input.onBlur}
      onFocus={input.onFocus}
      id={id}
      size={size}
      name={input.name}
      onRenderLabel={(prop, defaultRender) => {
        if (!!label && defaultRender) {
          return defaultRender(prop);
        } else {
          return null;
        }
      }}
      styles={styles}
    />
  );
};

/**
 * Use ChoiceGroupOptionField with final-form.
 * @param props
 */
export const ChoiceGroupOptionField: React.FC<
  ChoiceGroupOptionFieldProps
> = props => <Field {...props} component={ChoiceGroupOptionAdapter} />;
